import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Loader2, Users } from 'lucide-react';
import { usePipeline } from '../../hooks/usePipeline';
import { PipelineColumn } from './PipelineColumn';
import { PipelineCandidateCard } from './PipelineCandidateCard';
import type { PipelineEntry } from '../../types/pipeline';

interface PipelineKanbanViewProps {
  missionId: number;
}

/**
 * PipelineKanbanView — root ATS pipeline board.
 *
 * Architecture:
 * - usePipeline(missionId) fetches entries + columns from Supabase
 * - DndContext wraps all columns; onDragEnd calls updateStage (PIPE-03)
 * - Each column = PipelineColumn (useDroppable + SortableContext)
 * - Each card = PipelineCandidateCard (useSortable + click navigation)
 * - Optimistic update in usePipeline ensures immediate visual feedback
 *
 * Inter-column drag only — intra-column order not persisted in v1 (RESEARCH.md anti-pattern).
 */
export const PipelineKanbanView: React.FC<PipelineKanbanViewProps> = ({ missionId }) => {
  const { entries, columns, isLoading, error, updateStage } = usePipeline(missionId);
  const [activeDragEntry, setActiveDragEntry] = useState<PipelineEntry | null>(null);

  // Group entries by stage (column slug) — recomputed on entries change
  const entriesByColumn = useMemo(() => {
    const map: Record<string, PipelineEntry[]> = {};
    for (const col of columns) {
      map[col.slug] = [];
    }
    for (const entry of entries) {
      if (map[entry.stage] !== undefined) {
        map[entry.stage].push(entry);
      } else {
        // Unknown stage — put in first column (graceful degradation)
        const firstSlug = columns[0]?.slug;
        if (firstSlug) {
          map[firstSlug] = [...(map[firstSlug] ?? []), entry];
        }
      }
    }
    return map;
  }, [entries, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // 5px before DnD activates — allows click events to fire normally (Pitfall 3 + UI-SPEC)
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const matchId = Number(event.active.id);
    const entry = entries.find((e) => e.matchId === matchId) ?? null;
    setActiveDragEntry(entry);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragEntry(null);
    const { active, over } = event;
    if (!over) return;

    const matchId = Number(active.id);
    const overId = String(over.id);

    // Determine target column slug:
    // 'over' can be a column slug (useDroppable id) OR a card matchId (useSortable id).
    // If over.id is a column slug → use it directly.
    // If over.id is a numeric matchId → find that card's current stage.
    const isColumnSlug = columns.some((col) => col.slug === overId);
    const targetColumnSlug = isColumnSlug
      ? overId
      : entries.find((e) => String(e.matchId) === overId)?.stage ?? null;

    if (!targetColumnSlug) return;

    // Find source entry to verify stage actually changed
    const sourceEntry = entries.find((e) => e.matchId === matchId);
    if (!sourceEntry || sourceEntry.stage === targetColumnSlug) return;

    // PIPE-03: updateStage triggers optimistic update + Supabase persist
    updateStage(matchId, targetColumnSlug);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={20} className="animate-spin text-slate-400 mr-2" />
        <span className="text-sm text-slate-400 font-medium">Chargement du pipeline...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-red-500 font-medium">{error}</p>
      </div>
    );
  }

  // Empty board state (no candidates in pipeline yet)
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <Users size={40} className="mb-3 text-slate-300" />
        <p className="font-bold text-slate-600 mb-1">Aucun candidat dans ce pipeline</p>
        <p className="text-sm text-center max-w-xs">
          Ajoutez des candidats depuis l&apos;onglet Matching pour suivre leur avancement.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Kanban Board — horizontal scroll on small screens */}
      <div className="flex gap-4 overflow-x-auto pb-4 items-start">
        {columns.map((col) => (
          <PipelineColumn
            key={col.slug}
            column={col}
            entries={entriesByColumn[col.slug] ?? []}
            missionId={missionId}
          />
        ))}
      </div>

      {/* DragOverlay: renders a static clone of the dragged card at reduced opacity */}
      <DragOverlay>
        {activeDragEntry ? (
          <PipelineCandidateCard
            entry={activeDragEntry}
            missionId={missionId}
            isOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
